import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { NotifPollingService } from '../../../core/services/notif-polling.service';
import { DataService } from '../../../core/services/data.service';
import { ContentHeightService } from '../../../core/services/content-height.service';
import { SimulationNotification } from '../../../core/models/SimulationNotification';
import { Subject } from 'rxjs';
import { map, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import {isEqual} from 'lodash-es';
import { SimulationSummaryModalComponent } from '../simulation-summary-modal/simulation-summary-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { Store } from '../../../store';

/**
 * Sliding notification panel on the right side of the screen.
 *
 * @export
 * @class NotificationPanelComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'app-notification-panel',
    templateUrl: './notification-panel.component.html',
    styleUrls: ['./notification-panel.component.scss'],
    standalone: false
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
    /**
     * The "active" field is set if the notification panel is visible.
     *
     * @type {boolean}
     * @memberof NotificationPanelComponent
     */
    active = false;

    /**
     * Once the notification panel is hidden(active -> false), we remove it from the DOM.
     *
     * @type {boolean}
     * @memberof NotificationPanelComponent
     */
    deletedPanel = true;

    /**
     * Unsubscribe all the observables of the component on exit using a generic trigger.
     *
     * @private
     * @type {Subject<void>}
     * @memberof NotificationPanelComponent
     */
    private unsubscribe: Subject<void> = new Subject();

    /**
     * List of notifications displayed at the top of the panel.
     *
     * @type {SimulationNotification[]}
     * @memberof NotificationPanelComponent
     */
    queuedEntries: SimulationNotification[] = [];

    /**
     * List of notifications displayed at the bottom of the panel.
     *
     * @type {SimulationNotification[]}
     * @memberof NotificationPanelComponent
     */
    completedEntries: SimulationNotification[] = [];

    /**
     * The list of notifications that have never been shown to the user.
     *
     * @type {number}
     * @memberof NotificationPanelComponent
     */
    newEntriesNum: number = 0;

    /**
     * Height of the <content> component.
     *
     * @type {number}
     * @memberof NotificationPanelComponent
     */
    contentHeight: number = 0;

    /**
     * Creates an instance of NotificationPanelComponent.*
     *
     * @param {NotifPollingService} pollingService
     * @param {DataService} dataService
     * @param {ContentHeightService} contentHeightService
     * @param {NgbModal} modalService
     * @param {Router} router
     * @param {Store} store
     * @memberof NotificationPanelComponent
     */
    constructor(
        private pollingService: NotifPollingService,
        private dataService: DataService,
        private contentHeightService: ContentHeightService,
        private modalService: NgbModal,
        private router: Router,
        private store: Store
    ) {}

    /**
     * Subscribe to required observables during component setup.
     *
     * @memberof NotificationPanelComponent
     */
    ngOnInit() {

        this.contentHeightService.contentHeight$.pipe(takeUntil(this.unsubscribe))
                                                .subscribe((height: number) => this.contentHeight = height);

        /* Get notiification list from the notificaction cache. */
        this.pollingService.publishedNotifs$
            .pipe(
                map((items: SimulationNotification[]) => items.filter((item: SimulationNotification) => item.isVisible)),
                distinctUntilChanged((prev: SimulationNotification[],curr: SimulationNotification[]) => isEqual(prev, curr))
            )
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((items: SimulationNotification[]) => {
                this.queuedEntries = items.filter(
                    item =>
                        item.status === 'Queued' || item.status === 'Processing'
                );
                this.completedEntries = items.filter(
                    item => item.status === 'Completed' || item.status === 'Error'
                );

                this.newEntriesNum = items.filter(item => item.isNew).length;

                if ((this.queuedEntries.length === 0) && (this.completedEntries.length === 0)) {
                    this.active = false;
                }
            });
        /* Show the panel on demand. */
        this.pollingService.showNotifPanel$
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => {
                this.setActivePanel();
            });
    }

    /**
     * Stop timers when deleting the notification panel.
     *
     * @memberof NotificationPanelComponent
     */
    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    /**
     * Hide/show the panel.
     *
     * Make sure that the panel is already/still added to the DOM when running the CSS animations.
     *
     * @memberof NotificationPanelComponent
     */
    toggleActivePanel(): void {
        if (!this.active) {
            /* Add the panel to the DOM immediately, it will then slide into view. */
            this.deletedPanel = false;
            /* Once shown, notification items are no longer 'new'. */
            this.unsetNew(this.queuedEntries);
            this.unsetNew(this.completedEntries);
        }
        setTimeout(() => {
            this.active = !this.active;
            /* Remove the panel from the DOM once it has slided out of view. */
            if (!this.active) {
                setTimeout(() => (this.deletedPanel = true), 500);
            }
        }, 100);
    }

    /**
     * Make sure that the panel is visible.
     *
     * @memberof NotificationPanelComponent
     */
    setActivePanel(): void {
        if (!this.active) {
            this.toggleActivePanel();
        } else {
            /* Once shown, notification items are no longer 'new'. */
            this.unsetNew(this.queuedEntries);
            this.unsetNew(this.completedEntries);
        }
    }

    /**
     * Helper function to write back notifications to the backend when they lose the 'isNew' flag.
     *
     * @private
     * @memberof NotificationPanelComponent
     */
    public unsetNew(items: SimulationNotification[]): void {
        items.map((item: SimulationNotification) => item.isNew = false);
        this.pollingService.updateExistingNotifications(items, (item:SimulationNotification) => this.newEntriesNum -= 1);
    }
    /**
     * Hide a notification and update the cache.
     *
     * @param {SimulationNotification} notif
     * @memberof NotificationPanelComponent
     */
    onClose(notif: SimulationNotification) {
        notif.isVisible = false;
        this.pollingService.updateExistingNotifications([notif]);
        this.pollingService.broadcastNotificationList();
    }

    /**
     * Hide a list of notifications items, and update the cache.
     *
     * @param {SimulationNotification[]} notifs
     * @memberof NotificationPanelComponent
     */
    onCloseAll(notifs: SimulationNotification[]) {
        notifs.map((item: SimulationNotification) => item.isVisible = false);
        this.pollingService.updateExistingNotifications(notifs);
        this.pollingService.broadcastNotificationList();
    }

    /**
     * make sure that the panel is not shorter than the document.
     *
     * @returns {number}
     * @memberof NotificationPanelComponent
     */
    computeHeight(): number {
        let ret: number;
        // const rect = document.documentElement.getBoundingClientRect();
        if (this.contentHeight>window.innerHeight) {
            ret = this.contentHeight - 75;
        } else {
            ret = window.innerHeight - 75;
        }
        return ret;
    }


    doView(item: SimulationNotification) {
        if (item.status === 'Completed') {
            const url = `/entitySummary/${item.entityID}`;
            this.store.value.urlModelToggle[url] = true;
            this.store.value.modellingConfig.modelId = item.modelID;
            this.store.set('urlModelToggle', this.store.value.urlModelToggle);
            this.store.set('modellingConfig', this.store.value.modellingConfig);
            this.router.navigateByUrl(url).then();
            this.toggleActivePanel();
        } else {
            this.showDetails(item);
        }
    }

    public showDetails(item: SimulationNotification) {
        const modalRef = this.modalService.open(SimulationSummaryModalComponent, { centered: true, windowClass: 'model-instruments-modal' });
        modalRef.result.then().catch(err => {});
        modalRef.componentInstance.modelID = item.modelID;
        modalRef.componentInstance.entityName = item.entity;
    }

    /***************************************************************
     *
     *                          TEMPORARY
     *
     ***************************************************************/
    reset() {
        this.pollingService.restartNotificationPolling();
    }
}